<template>
  <!-- Icon template file -->
  <div class="icon-container">
    <div ref="svgWrapper" :class="{ rotate: rotate }" v-html="source"></div>
    <div v-if="badges" class="icon-badges"></div>
  </div>
</template>
<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from 'vue'
import { getCache, setCache } from './cache'

defineOptions({ name: 'CartoComponentIcon' })

const props = withDefaults(
  defineProps<{
    /**
     * Sets the icon source
     */
    icon: string
    size?: string | string[]
    fill?: string
    stroke?: string
    rotate?: boolean
    badges?: boolean
  }>(),
  {
    size: '20px',
    fill: '',
    rotate: false,
    badges: false,
  },
)

const source = ref<string | undefined>(undefined)
const svgWrapper = ref<HTMLElement | null>(null)

let iconValue = props.icon
let fillValue = props.fill

onMounted(() => {
  getIcon()
})

watch(
  () => props.icon,
  (icon) => {
    iconValue = icon
    getIcon()
  },
)

watch(
  () => props.fill,
  (fill) => {
    fillValue = fill
    drawIcon()
  },
)

async function getIcon(): Promise<void> {
  if (isExternal(iconValue)) {
    const request = iconValue
    if (getCache(request)) {
      const response = await getCache(request)
      setData(response as string)
      return
    }
    getSource(request)
    return
  }

  // Use import.meta.glob for local icons
  const icons = import.meta.glob('@/assets/icons/*.svg', {
    eager: true,
    query: '?url',
    import: 'default',
  })

  // Find the correct icon by matching the filename
  const iconKey = Object.keys(icons).find((key) => key.includes(iconValue))
  const iconUrl = iconKey ? (icons[iconKey] as string) : null

  if (iconUrl) {
    if (getCache(iconUrl)) {
      const response = await getCache(iconUrl)
      setData(response as string)
      return
    }
    getSource(iconUrl)
  } else {
    console.warn(
      `Icon not found: ${iconValue}. Available icons:`,
      Object.keys(icons),
    )
  }
}

async function getSource(request: string): Promise<void> {
  const promise = requestSource(request)
  setCache(request, promise)
  const response = await promise
  setCache(request, response)
  setData(response as string)
}

async function requestSource(url: string): Promise<string | undefined> {
  try {
    const response = await window.fetch(url)
    return response.text()
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
  }
}

function setData(svg: string): void {
  source.value = svg
  nextTick(() => drawIcon())
}

function drawIcon(): void {
  if (!svgWrapper.value) return
  const svg = svgWrapper.value.querySelector('svg')
  if (svg) {
    if (typeof props.size === 'string') {
      svg.setAttribute('width', props.size)
      svg.setAttribute('height', props.size)
    } else {
      svg.setAttribute('width', props.size[0])
      svg.setAttribute('height', props.size[1])
    }
    if (fillValue) {
      svg.setAttribute('fill', fillValue)
      svg
        .querySelectorAll('path')
        .forEach((path) => path.setAttribute('fill', fillValue))
    }
    if (props.stroke) {
      svg.setAttribute('stroke', props.stroke)
    }
  }
}

function isExternal(value: string): boolean {
  return value.indexOf('http') !== -1
}
</script>
<style scoped lang="scss" src="./icon.scss"></style>
